import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useSystemSettings } from '../../../hooks/useSystemSettings';

interface AILog {
  id: string;
  user_id: string;
  question: string | null;
  response_summary: string | null;
  tokens_used: number;
  provider: string | null;
  model: string | null;
  success: boolean;
  error_message: string | null;
  session_id: string | null;
  created_at: string;
  profiles?: {
    full_name: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null;
}

interface DailyUsage {
  date: string;
  count: number;
  tokens: number;
  success: number;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const fmtShort = (iso: string) => {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
};

type Filter = 'all' | 'success' | 'error';
type Period = '7' | '14' | '30';

const AdminPandaAIPage = () => {
  const { settings } = useSystemSettings();
  const assistantName = settings.ai_assistant_name || 'Panda AI';

  const [logs, setLogs] = useState<AILog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [period, setPeriod] = useState<Period>('14');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    const since = new Date();
    since.setDate(since.getDate() - parseInt(period));

    try {
      // Query with profiles join (FK now exists: ai_usage_logs.user_id -> profiles.id)
      let query = supabase
        .from('ai_usage_logs')
        .select('*, profiles(full_name, email, avatar_url)')
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);

      if (filter === 'success') query = query.eq('success', true);
      if (filter === 'error') query = query.eq('success', false);

      const { data, error: queryError } = await query;

      if (queryError) {
        console.error('[PandaAI Logs] Query error:', queryError);

        // Fallback: query without profiles join
        let fallbackQuery = supabase
          .from('ai_usage_logs')
          .select('*')
          .gte('created_at', since.toISOString())
          .order('created_at', { ascending: false })
          .limit(500);

        if (filter === 'success') fallbackQuery = fallbackQuery.eq('success', true);
        if (filter === 'error') fallbackQuery = fallbackQuery.eq('success', false);

        const { data: fallbackData, error: fallbackError } = await fallbackQuery;

        if (fallbackError) {
          console.error('[PandaAI Logs] Fallback query error:', fallbackError);
          setError(`Erro ao carregar logs: ${fallbackError.message}`);
          setLogs([]);
        } else {
          console.log(`[PandaAI Logs] Fallback query returned ${fallbackData?.length ?? 0} rows`);
          setLogs((fallbackData as AILog[]) || []);
        }
      } else {
        console.log(`[PandaAI Logs] Query returned ${data?.length ?? 0} rows`);
        setLogs((data as AILog[]) || []);
      }
    } catch (e) {
      console.error('[PandaAI Logs] Exception:', e);
      setError('Erro inesperado ao carregar logs.');
      setLogs([]);
    } finally {
      setLoading(false);
      setPage(0);
    }
  }, [filter, period]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Stats
  const totalQuestions = logs.length;
  const totalTokens = logs.reduce((s, l) => s + (l.tokens_used || 0), 0);
  const successCount = logs.filter(l => l.success).length;
  const errorCount = totalQuestions - successCount;
  const successRate = totalQuestions > 0 ? Math.round((successCount / totalQuestions) * 100) : 0;
  const uniqueUsers = new Set(logs.map(l => l.user_id)).size;
  const avgTokens = totalQuestions > 0 ? Math.round(totalTokens / totalQuestions) : 0;
  const days = parseInt(period);

  // Daily usage for chart
  const dailyMap: Record<string, DailyUsage> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().split('T')[0];
    dailyMap[key] = { date: key, count: 0, tokens: 0, success: 0 };
  }
  logs.forEach(l => {
    const key = l.created_at.split('T')[0];
    if (dailyMap[key]) {
      dailyMap[key].count++;
      dailyMap[key].tokens += l.tokens_used || 0;
      if (l.success) dailyMap[key].success++;
    }
  });
  const dailyData = Object.values(dailyMap);
  const maxCount = Math.max(...dailyData.map(d => d.count), 1);

  // Per user breakdown
  const userMap: Record<string, { name: string; email: string; avatar: string | null; count: number; tokens: number; success: number }> = {};
  logs.forEach(l => {
    const key = l.user_id;
    if (!userMap[key]) {
      userMap[key] = {
        name: l.profiles?.full_name || 'Usuário',
        email: l.profiles?.email || '',
        avatar: l.profiles?.avatar_url || null,
        count: 0, tokens: 0, success: 0,
      };
    }
    userMap[key].count++;
    userMap[key].tokens += l.tokens_used || 0;
    if (l.success) userMap[key].success++;
  });
  const topUsers = Object.entries(userMap).sort((a, b) => b[1].count - a[1].count).slice(0, 5);

  // Provider breakdown
  const providerMap: Record<string, number> = {};
  logs.forEach(l => {
    const p = l.provider || 'openai';
    providerMap[p] = (providerMap[p] || 0) + 1;
  });

  // Filtered logs for table
  const filteredLogs = logs.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      l.question?.toLowerCase().includes(s) ||
      l.profiles?.full_name?.toLowerCase().includes(s) ||
      l.profiles?.email?.toLowerCase().includes(s) ||
      l.model?.toLowerCase().includes(s) ||
      l.provider?.toLowerCase().includes(s)
    );
  });
  const pagedLogs = filteredLogs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);

  const chartLabels = (() => {
    if (dailyData.length < 4) return [];
    const step = Math.floor(dailyData.length / 3);
    return [0, step, step * 2, dailyData.length - 1].map(i => fmtShort(dailyData[i].date));
  })();

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-white/40 text-[13px] mb-3">
          <Link to="/admin/integracoes" className="hover:text-white transition-colors cursor-pointer">Integrações</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white">{assistantName} — Logs de Uso</span>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-[24px] font-700 text-white flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <i className="ri-robot-2-line text-emerald-400 text-[18px]" />
              </div>
              {assistantName} — Logs de Uso
            </h1>
            <p className="text-white/40 text-[14px] mt-1">Monitoramento do assistente financeiro IA</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(['7', '14', '30'] as Period[]).map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-xl text-[13px] font-600 transition-colors cursor-pointer whitespace-nowrap ${period === p ? 'bg-emerald-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'}`}>
                {p}d
              </button>
            ))}
            <button onClick={fetchLogs}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-xl text-[13px] transition-colors cursor-pointer whitespace-nowrap">
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />Atualizar
            </button>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && !loading && (
        <div className="mb-5 flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          <i className="ri-error-warning-line text-red-400 text-lg" />
          <div>
            <p className="text-[13px] text-red-300 font-600">Erro ao carregar logs</p>
            <p className="text-[12px] text-red-400/70">{error}</p>
          </div>
          <button onClick={fetchLogs} className="ml-auto px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-[12px] transition-colors cursor-pointer whitespace-nowrap">
            Tentar novamente
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-3xl text-emerald-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Total de perguntas', value: totalQuestions.toLocaleString('pt-BR'), icon: 'ri-question-answer-line', color: 'bg-emerald-600', sub: `últimos ${period} dias` },
              { label: 'Tokens consumidos', value: totalTokens.toLocaleString('pt-BR'), icon: 'ri-cpu-line', color: 'bg-violet-600', sub: `~${avgTokens} por pergunta` },
              { label: 'Taxa de sucesso', value: `${successRate}%`, icon: 'ri-checkbox-circle-line', color: successRate >= 90 ? 'bg-emerald-600' : successRate >= 70 ? 'bg-amber-500' : 'bg-red-600', sub: `${successCount} ok / ${errorCount} erros` },
              { label: 'Usuários ativos', value: uniqueUsers.toLocaleString('pt-BR'), icon: 'ri-team-line', color: 'bg-cyan-600', sub: 'usuários únicos' },
              { label: 'Média diária', value: days > 0 ? (totalQuestions / days).toFixed(1) : '0', icon: 'ri-bar-chart-line', color: 'bg-amber-500', sub: 'perguntas/dia' },
            ].map(c => (
              <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-[12px] text-white/40">{c.label}</p>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${c.color} shrink-0`}>
                    <i className={`${c.icon} text-white text-[15px]`} />
                  </div>
                </div>
                <p className="text-[24px] font-700 text-white leading-tight">{c.value}</p>
                {c.sub && <p className="text-[11px] text-white/30">{c.sub}</p>}
              </div>
            ))}
          </div>

          {/* Chart + Top Users + Provider Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            {/* Daily chart */}
            <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-[15px] font-600 text-white">Uso diário</h2>
                  <p className="text-[12px] text-white/40">Perguntas enviadas por dia</p>
                </div>
                <span className="text-[12px] px-3 py-1 bg-emerald-600/20 text-emerald-400 rounded-xl font-600">
                  {totalQuestions} total
                </span>
              </div>
              {totalQuestions === 0 ? (
                <div className="flex flex-col items-center justify-center h-24 text-white/30 gap-2">
                  <i className="ri-bar-chart-2-line text-3xl" />
                  <p className="text-[13px]">Nenhum uso neste período</p>
                </div>
              ) : (
                <>
                  <div className="flex items-end gap-[3px] h-[100px]">
                    {dailyData.map(day => {
                      const heightPct = Math.max((day.count / maxCount) * 100, day.count > 0 ? 8 : 2);
                      const successPct = day.count > 0 ? (day.success / day.count) * 100 : 0;
                      return (
                        <div key={day.date} className="flex-1 rounded-t transition-colors cursor-pointer group relative"
                          style={{ height: `${heightPct}%`, backgroundColor: day.count > 0 ? `rgba(16,185,129,${0.3 + (successPct / 100) * 0.5})` : 'rgba(255,255,255,0.06)' }}>
                          {day.count > 0 && (
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[10px] text-white/80 bg-black/70 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                              {fmtShort(day.date)}: {day.count} ({day.tokens.toLocaleString('pt-BR')} tokens)
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-[11px] text-white/30">
                    {chartLabels.map((l, i) => <span key={i}>{l}</span>)}
                  </div>
                </>
              )}
            </div>

            {/* Right column: top users + provider */}
            <div className="flex flex-col gap-4">
              {/* Top users */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex-1">
                <h2 className="text-[15px] font-600 text-white mb-4">Top usuários</h2>
                {topUsers.length === 0 ? (
                  <p className="text-[13px] text-white/30 text-center py-6">Sem dados</p>
                ) : (
                  <div className="space-y-3">
                    {topUsers.map(([uid, u], idx) => (
                      <div key={uid} className="flex items-center gap-3">
                        <span className={`w-5 text-[11px] font-700 text-center shrink-0 ${idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-white/60' : 'text-white/30'}`}>
                          #{idx + 1}
                        </span>
                        <div className="w-7 h-7 rounded-full bg-emerald-600/30 flex items-center justify-center shrink-0 overflow-hidden">
                          {u.avatar ? (
                            <img src={u.avatar} alt={u.name} className="w-7 h-7 object-cover object-top" />
                          ) : (
                            <span className="text-white text-[10px] font-700">{u.name.slice(0, 2).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12px] text-white font-600 truncate">{u.name}</p>
                          <p className="text-[10px] text-white/40 truncate">{u.email}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[13px] font-700 text-emerald-400">{u.count}</p>
                          <p className="text-[10px] text-white/30">{u.tokens.toLocaleString('pt-BR')} tok</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Provider breakdown */}
              {Object.keys(providerMap).length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <h2 className="text-[13px] font-600 text-white mb-3">Providers</h2>
                  <div className="space-y-2">
                    {Object.entries(providerMap).sort((a, b) => b[1] - a[1]).map(([prov, cnt]) => (
                      <div key={prov} className="flex items-center justify-between">
                        <span className="text-[12px] text-white/60 font-mono capitalize">{prov}</span>
                        <span className="text-[12px] font-600 text-white">{cnt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Filters + Table */}
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-wrap gap-3">
              <h2 className="text-[15px] font-600 text-white">
                Histórico de perguntas
                {filteredLogs.length > 0 && (
                  <span className="ml-2 text-[12px] text-white/30 font-400">({filteredLogs.length} registros)</span>
                )}
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                  <i className="ri-search-line text-white/30 text-sm" />
                  <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Buscar pergunta ou usuário..."
                    className="bg-transparent text-white text-[13px] outline-none placeholder:text-white/20 w-48" />
                  {search && (
                    <button onClick={() => setSearch('')} className="text-white/30 hover:text-white cursor-pointer">
                      <i className="ri-close-line text-sm" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
                  {([['all', 'Todos'], ['success', 'Sucesso'], ['error', 'Erros']] as [Filter, string][]).map(([v, l]) => (
                    <button key={v} onClick={() => { setFilter(v); setPage(0); }}
                      className={`px-3 py-1.5 rounded-lg text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filter === v ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filteredLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-white/30 gap-3">
                <i className="ri-chat-history-line text-4xl" />
                <p className="text-[14px]">
                  {totalQuestions === 0 ? 'Nenhuma interação registrada neste período' : 'Nenhum log encontrado para este filtro'}
                </p>
                {totalQuestions === 0 && (
                  <p className="text-[12px] text-white/20 text-center max-w-xs">
                    Os logs aparecem aqui após o primeiro uso do assistente IA
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        {['Usuário', 'Pergunta', 'Resposta', 'Tokens', 'Provider / Modelo', 'Status', 'Data'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-[11px] font-600 text-white/30 uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {pagedLogs.map(log => (
                        <tr key={log.id} className="hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-emerald-600/20 flex items-center justify-center shrink-0 overflow-hidden">
                                {log.profiles?.avatar_url ? (
                                  <img src={log.profiles.avatar_url} alt="" className="w-7 h-7 object-cover object-top" />
                                ) : (
                                  <span className="text-white text-[9px] font-700">{(log.profiles?.full_name || 'U').slice(0, 2).toUpperCase()}</span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-[12px] text-white font-600 truncate max-w-[120px]">{log.profiles?.full_name || '—'}</p>
                                <p className="text-[10px] text-white/30 truncate max-w-[120px]">{log.profiles?.email || ''}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            <p className="text-[12px] text-white/70 truncate" title={log.question || ''}>{log.question || '—'}</p>
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            <p className="text-[12px] text-white/40 truncate" title={log.response_summary || ''}>
                              {log.success ? (log.response_summary || '—') : (
                                <span className="text-red-400/70" title={log.error_message || ''}>{log.error_message || 'Erro'}</span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-[12px] font-600 text-white/70">{(log.tokens_used || 0).toLocaleString('pt-BR')}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              {log.provider && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-600/15 text-emerald-400 rounded font-mono capitalize w-fit">{log.provider}</span>
                              )}
                              <span className="text-[11px] px-2 py-0.5 bg-white/8 text-white/50 rounded-lg font-mono">{log.model || '—'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {log.success ? (
                              <span className="flex items-center gap-1 text-[11px] font-600 text-emerald-400">
                                <i className="ri-checkbox-circle-fill" />OK
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[11px] font-600 text-red-400" title={log.error_message || ''}>
                                <i className="ri-close-circle-fill" />Erro
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-[11px] text-white/40">{fmtDate(log.created_at)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-white/10">
                    <p className="text-[12px] text-white/40">
                      {filteredLogs.length} registros — página {page + 1} de {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-lg text-[12px] transition-colors cursor-pointer disabled:opacity-30">
                        <i className="ri-arrow-left-s-line" />
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = totalPages <= 5 ? i : Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                        return (
                          <button key={pageNum} onClick={() => setPage(pageNum)}
                            className={`w-7 h-7 rounded-lg text-[12px] transition-colors cursor-pointer ${pageNum === page ? 'bg-emerald-600 text-white font-600' : 'bg-white/5 hover:bg-white/10 text-white/40 hover:text-white'}`}>
                            {pageNum + 1}
                          </button>
                        );
                      })}
                      <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-lg text-[12px] transition-colors cursor-pointer disabled:opacity-30">
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPandaAIPage;
